import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { Col, Row } from 'reactstrap';
import { Form } from 'formik';
import PropTypes from 'prop-types';
import Datetime from 'react-datetime';
import moment from 'moment';
import classNames from 'classnames';
import get from 'lodash/get';
import FormSelectField from '../../common/components/formFields/FormSelectField';
import { constantToSelect } from '../../common/helpers/constantToSelect';
import FormCurrencyInputFieldFixed from '../../common/components/formFields/FormCurrencyInputFieldFixed';
import FormDatePickerField from '../../common/components/formFields/FormDatePickerField';
import { REVERSE_DASH_DATETIME_FORMAT } from '../../common/utils/date/Date.utils';
import FormCheckboxFieldFixed from '../../common/components/formFields/FormCheckboxFieldFixed';
import {
  AuctionStatusType,
  AuctionStatusTypeName,
  AvailableAuctionStatuses,
} from '../../constants/AuctionStatusType';
import FormGroupRadioField from '../../common/components/formFields/FormGroupRadioField';
import { AuctionPublishTypeName } from '../../constants/AuctionPublishType';
import { DueInDateType } from '../../constants/DueInDateType';
import AddVehicleContentWrapper from '../AddVehicleContentWrapper';
import SettingsSectionHeader from '../../settings/SettingsSectionHeader';
import calendarIcon from '../../assets/img/icons/calendar_month.svg';
import poundIcon from '../../assets/img/icons/poundicon.svg';
import '../../assets/css/componentStyles/FormValidation.css';

export function AuctionDateTimePicker({
  label,
  name,
  form,
  required = false,
  labelVariant = 'bordered',
  placeholder,
  className = '',
  disabled = false,
  'aria-describedby': ariaDescribedBy,
}) {
  const inputRef = useRef(null);
  const error = get(form.errors, name);
  const touched = get(form.touched, name);
  const rawValue = get(form.values, name);
  const value = disabled || !rawValue ? '' : moment(rawValue);
  const isBordered = labelVariant === 'bordered';
  const labelClassName = classNames('form-input-field-label', {
    'label-required': required && isBordered,
  });

  const fieldId = `${name}-field`;
  const errorId = `${name}-error`;

  const handleChange = useCallback(
    (option) => {
      form.setFieldValue(name, moment(option).format(REVERSE_DASH_DATETIME_FORMAT));
    },
    [form, name],
  );

  const handleBlur = useCallback(() => {
    form.setFieldTouched(name, true);
  }, [form, name]);

  const handleCalendarIconClick = useCallback(() => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  }, [disabled]);

  const hasError = (form.submitCount > 0 || touched) && error;

  return (
    <Row>
      <Col>
        <div
          className={classNames(
            'form-group',
            {
              'form-group--bordered-label': isBordered,
            },
            className,
          )}
        >
          <label
            htmlFor={fieldId}
            className={`${labelClassName} ${disabled ? 'form-input-field-label__disabled' : ''}`}
            aria-label={required ? `${label} (required)` : label}
          >
            {label}
          </label>
          <div
            onClick={handleCalendarIconClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCalendarIconClick();
              }
            }}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={`Focus ${label || 'date picker'}`}
            style={{ cursor: disabled ? 'default' : 'pointer' }}
            className="calendar-icon-wrapper"
          >
            <img
              src={calendarIcon}
              alt=""
              className={`calendar-icon-launch ${disabled ? 'input-icon__disabled' : ''}`}
            />
          </div>
          <Datetime
            key={`${name}-${disabled}`}
            closeOnSelect={true}
            timeFormat={true}
            dateFormat="DD/MM/YYYY"
            inputProps={{
              id: fieldId,
              placeholder,
              className: classNames('form-control', {
                'is-invalid': hasError,
                'form-input-field-label__disabled': disabled,
              }),
              disabled,
              'aria-required': required,
              'aria-invalid': hasError,
              'aria-describedby': hasError ? errorId : ariaDescribedBy,
              ref: inputRef,
            }}
            value={value}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          {hasError && (
            <label
              id={errorId}
              className="form-input-field__label error-label"
              role="alert"
              aria-live="polite"
            >
              {error}
            </label>
          )}
        </div>
      </Col>
    </Row>
  );
}

function LaunchVehicle({
  form,
  organizationLocations = [],
  organizationGroup = null,
  className = '',
  labelVariant = 'bordered',
  setIsScheduledAuction,
  validationErrors = {},
  onValidate,
}) {
  // Extract errors from backend All key
  const getFieldServerError = (fieldName) => {
    // Corrected to look in the LAUNCH_VEHICLE section
    const launchErrors = validationErrors?.LAUNCH_VEHICLE;
    if (!launchErrors) return null;

    const backendFieldMappings = {
      vehicleLocation: 'descriptionAndSettings.locationId',
      auctionEndDate: 'descriptionAndSettings.auctionEndDate',
      type: 'descriptionAndSettings.type',
      autoAcceptPriceInPence: 'descriptionAndSettings.autoAcceptPriceInPence',
      minimumPriceInPence: 'descriptionAndSettings.minimumPriceInPence',
      photos: 'vehicleMedia.photos',
    };
    const backendKey = backendFieldMappings[fieldName];
    return backendKey ? launchErrors[backendKey] : null;
  };

  // Handle special error for missing end date
  const auctionEndDateError = (() => {
    if (validationErrors && validationErrors.code === 'AUCTION_END_TIME_NOT_PROVIDED') {
      return validationErrors.message || 'Auction end time not provided.';
    }
    return getFieldServerError('auctionEndDate');
  })();

  const auctionTypes = useMemo(
    () =>
      constantToSelect(
        Object.keys(AuctionStatusType)
          .filter((el) => AvailableAuctionStatuses.includes(el))
          .reduce(
            (obj, key) => ({
              ...obj,
              [key]: AuctionStatusTypeName[key],
            }),
            {},
          ),
      ),
    [],
  );

  const auctionPublishTypes = useMemo(() => constantToSelect(AuctionPublishTypeName), []);

  const preparedOrganizationLocations = useMemo(
    () =>
      organizationLocations?.map((item) => ({
        value: item?.id,
        label: item?.name,
      })) || [],
    [organizationLocations],
  );

  const formValues = useMemo(
    () => form.values.descriptionAndSettings || {},
    [form.values.descriptionAndSettings],
  );
  useEffect(() => {
    const typeValue = formValues.type?.value;
    if (typeValue === AuctionStatusType.BUY_NOW_ONLY && form.setFieldValue) {
      form.setFieldValue('descriptionAndSettings.type', null);
    }
  }, [formValues.type?.value, form.setFieldValue, form]);

  const currentAuctionType = useMemo(() => formValues.type?.value, [formValues.type?.value]);

  const showPhysicalReserve = useMemo(
    () => currentAuctionType === AuctionStatusType.PHYSICAL,
    [currentAuctionType],
  );

  const showTryingToDeal = useMemo(
    () => currentAuctionType === AuctionStatusType.TRYING_TO_DEAL,
    [currentAuctionType],
  );

  const showCustomDeliveryDate = useMemo(
    () =>
      showTryingToDeal &&
      formValues.tryingToDealDeliveryDateType?.value === DueInDateType.CUSTOM_DATE,
    [showTryingToDeal, formValues.tryingToDealDeliveryDateType?.value],
  );

  const handleStatusChange = useCallback(
    (e) => {
      form.setFieldValue('descriptionAndSettings.type', e);
      if (e.value === AuctionStatusType.TRYING_TO_DEAL) {
        form.setFieldValue('descriptionAndSettings.tryingToDealDeliveryDate', new Date());
      } else {
        form.setFieldValue('descriptionAndSettings.tryingToDealDeliveryDate', null);
      }
      form.setFieldValue('descriptionAndSettings.minimumPriceInPence', null);
      form.setFieldValue('descriptionAndSettings.autoAcceptPriceInPence', null);
    },
    [form],
  );

  const { setFieldValue, values } = form;
  const startAuctionImmediately = get(
    values,
    'descriptionAndSettings.startAuctionImmediately',
  );
  const termsAccepted = get(values, 'descriptionAndSettings.termsAccepted');

  // Update parent component when start auction immediately changes
  useEffect(() => {
    if (setIsScheduledAuction) {
      setIsScheduledAuction(startAuctionImmediately === false);
    }
  }, [startAuctionImmediately, setIsScheduledAuction]);

  useEffect(() => {
    if (startAuctionImmediately) {
      setFieldValue('descriptionAndSettings.auctionStartDate', null);
    }
  }, [startAuctionImmediately, setFieldValue]);

  useEffect(() => {
    if (!termsAccepted) {
      setFieldValue('descriptionAndSettings.buyNowPriceInPence', null);
    }
  }, [termsAccepted, setFieldValue]);

  return (
    <AddVehicleContentWrapper className={classNames('mb-5', className)}>
      <SettingsSectionHeader
        header="Auction Details"
        description="All vehicle details are now complete. Please select the parameters of your auction below."
      />
      <Form autoComplete="off" className="text-left mt-5" onBlur={onValidate}>
        <Row md={12}>
          <Col md={3} className="mb-3 mb-md-0">
            <FormSelectField
              label="Vehicle Location"
              name="descriptionAndSettings.vehicleLocation"
              options={preparedOrganizationLocations}
              required
              labelVariant={labelVariant}
              form={form}
              blurInputOnSelect={false}
            />
            {getFieldServerError('vehicleLocation') && (
              <div className="field-error-message">
                {getFieldServerError('vehicleLocation')}
              </div>
            )}
          </Col>
          <Col md={3} className="mb-3 mb-md-0">
            <FormCheckboxFieldFixed
              name="descriptionAndSettings.startAuctionImmediately"
              label="Auction starts immediately"
              className="launch-vehicle-checkbox-setting"
              form={form}
            />
          </Col>
          <Col md={3} className="mb-3 mb-md-0">
            <AuctionDateTimePicker
              label="Date & Time – Auction Starts"
              name="descriptionAndSettings.auctionStartDate"
              required
              labelVariant={labelVariant}
              form={form}
              disabled={!!get(form.values, 'descriptionAndSettings.startAuctionImmediately')}
            />
          </Col>
          <Col md={3}>
            <AuctionDateTimePicker
              label="Date & Time – Auction Ends"
              name="descriptionAndSettings.auctionEndDate"
              required
              labelVariant={labelVariant}
              form={form}
            />
            {auctionEndDateError && (
              <div className="field-error-message">{auctionEndDateError}</div>
            )}
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md={12}>
            <FormCheckboxFieldFixed
              name="descriptionAndSettings.standardAuctionAndBuyNow"
              label="STANDARD AUCTION"
              form={form}
              className="launch-vehicle-checkbox launch-vehicle-checkbox-setting"
            />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={4}>
            <FormSelectField
              name="descriptionAndSettings.type"
              label="Auction type"
              options={auctionTypes}
              setFieldValue={form.setFieldValue}
              handleChangeSideEffect={handleStatusChange}
              required
              labelVariant={labelVariant}
              form={form}
              blurInputOnSelect={false}
              disabled={!get(form.values, 'descriptionAndSettings.standardAuctionAndBuyNow')}
            />
            {getFieldServerError('type') && (
              <div className="field-error-message">{getFieldServerError('type')}</div>
            )}
          </Col>
          {showPhysicalReserve && (
            <Col md={4}>
              <FormCurrencyInputFieldFixed
                name="descriptionAndSettings.autoAcceptPriceInPence"
                label="Reserve Price"
                maxLength={14}
                required
                labelVariant={labelVariant}
                form={form}
                icon={poundIcon}
                iconClassName="calendar-icon"
                disabled={!get(form.values, 'descriptionAndSettings.standardAuctionAndBuyNow')}
              />
              {getFieldServerError('autoAcceptPriceInPence') && (
                <div className="field-error-message">
                  {getFieldServerError('autoAcceptPriceInPence')}
                </div>
              )}
            </Col>
          )}
          <Col md={4}>
            <FormCurrencyInputFieldFixed
              name="descriptionAndSettings.minimumPriceInPence"
              label="Minimum Price"
              maxLength={14}
              labelVariant={labelVariant}
              form={form}
              icon={poundIcon}
              iconClassName="calendar-icon"
              disabled={!get(form.values, 'descriptionAndSettings.standardAuctionAndBuyNow')}
            />
            {getFieldServerError('minimumPriceInPence') && (
              <div className="field-error-message">
                {getFieldServerError('minimumPriceInPence')}
              </div>
            )}
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={12}>
            <FormCheckboxFieldFixed
              className="launch-vehicle-checkbox launch-vehicle-checkbox-setting"
              name="descriptionAndSettings.termsAccepted"
              label="BUY NOW OFFER"
              form={form}
            />
          </Col>
          <Col md={4} className="mt-3">
            <FormCurrencyInputFieldFixed
              name="descriptionAndSettings.buyNowPriceInPence"
              label="Buy Now Price"
              maxLength={14}
              labelVariant={labelVariant}
              form={form}
              icon={poundIcon}
              iconClassName="calendar-icon"
              required
              disabled={!get(form.values, 'descriptionAndSettings.termsAccepted')}
            />
          </Col>
        </Row>

        <Row className="mt-3" />

        {showCustomDeliveryDate && (
          <Row className="mt-3">
            <Col md={4}>
              <FormDatePickerField
                label="Delivery date"
                name="descriptionAndSettings.tryingToDealDeliveryDate"
                readOnly
                isPastDateDisabled
                required
                labelVariant={labelVariant}
                form={form}
                icon={calendarIcon}
                iconClassName="calendar-icon"
              />
            </Col>
          </Row>
        )}

        {organizationGroup && (
          <Row className="mt-4">
            <Col md={12}>
              <FormGroupRadioField
                name="descriptionAndSettings.publishTarget"
                radios={auctionPublishTypes}
                classNames="auction-publish-radios"
                label="Publish Target"
                required
                form={form}
              />
            </Col>
          </Row>
        )}
      </Form>
    </AddVehicleContentWrapper>
  );
}

LaunchVehicle.propTypes = {
  form: PropTypes.object.isRequired,
  organizationLocations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      postalCode: PropTypes.shape({
        postcode: PropTypes.string,
      }),
    }),
  ),
  organizationGroup: PropTypes.object,
  className: PropTypes.string,
  labelVariant: PropTypes.oneOf(['bordered', 'standard', 'minimal']),
  setIsScheduledAuction: PropTypes.func,
  validationErrors: PropTypes.object,
  onValidate: PropTypes.func,
};

LaunchVehicle.defaultProps = {
  organizationLocations: [],
  organizationGroup: null,
  className: '',
  labelVariant: 'bordered',
  setIsScheduledAuction: null,
  validationErrors: {},
  onValidate: () => {},
};

AuctionDateTimePicker.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
    values: PropTypes.object,
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
    submitCount: PropTypes.number,
  }).isRequired,
  required: PropTypes.bool,
  labelVariant: PropTypes.oneOf(['bordered', 'standard', 'minimal']),
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  'aria-describedby': PropTypes.string,
};

AuctionDateTimePicker.defaultProps = {
  required: false,
  labelVariant: 'bordered',
  placeholder: '',
  className: '',
  disabled: false,
  'aria-describedby': undefined,
};

export default LaunchVehicle;
